import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Camera, Keyboard, X } from 'lucide-react';

/**
 * QRScanner
 * - Opens a camera-based QR scanner (html5-qrcode) inside a modal.
 * - Falls back to a manual entry input if camera is denied / unsupported.
 * - Calls onScan(qrData) when a valid code is decoded.
 */
export default function QRScanner({
  open,
  onOpenChange,
  onScan,
  title = 'Scan QR Code',
  description = 'Point the camera at the QR code.',
  dataTestId = 'qr-scanner',
}) {
  const containerId = useRef(`qr-reader-${Math.random().toString(36).slice(2, 9)}`).current;
  const scannerRef = useRef(null);
  const [mode, setMode] = useState('camera'); // 'camera' | 'manual'
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!open || mode !== 'camera') return;
      setError('');
      try {
        const mod = await import('html5-qrcode');
        const { Html5Qrcode } = mod;
        if (cancelled) return;

        const node = document.getElementById(containerId);
        if (!node) return;
        // Safely clear the container without touching innerHTML
        while (node.firstChild) node.removeChild(node.firstChild);

        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (!decodedText) return;
            try { scanner.pause(true); } catch (_e) { /* noop */ }
            onScan?.(decodedText);
          },
          () => { /* ignore per-frame decode errors */ }
        );
      } catch (e) {
        setError(
          'Camera unavailable. Please allow camera access or use manual entry below.'
        );
      }
    }

    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch (_e) {} });
        scannerRef.current = null;
      }
    };
  }, [open, mode, containerId, onScan]);

  const submitManual = () => {
    if (!manual.trim()) return;
    onScan?.(manual.trim());
    setManual('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#2D2D2D] text-white border-[#C9A962]"
        data-testid={`${dataTestId}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="text-[#C9A962] flex items-center gap-2">
            <Camera className="h-5 w-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">{description}</DialogDescription>
        </DialogHeader>

        {mode === 'camera' && (
          <div>
            <div
              id={containerId}
              className="w-full aspect-square rounded-lg overflow-hidden border border-[#C9A962]/40 bg-black"
              data-testid={`${dataTestId}-camera`}
            />
            {error && (
              <p className="text-sm text-red-400 mt-3" data-testid={`${dataTestId}-error`}>
                {error}
              </p>
            )}
            <Button
              variant="outline"
              className="w-full mt-4 border-[#C9A962] text-[#C9A962]"
              onClick={() => setMode('manual')}
              data-testid={`${dataTestId}-manual-toggle`}
            >
              <Keyboard className="h-4 w-4 mr-2" /> Enter code manually
            </Button>
          </div>
        )}

        {mode === 'manual' && (
          <div className="space-y-3">
            <Label className="text-zinc-300">QR code value</Label>
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="NECS:PROP:... or NECS:UNIT:... or NECS:STAFF:..."
              className="bg-[#3D3D3D] border-[#4a4a4a] text-white"
              data-testid={`${dataTestId}-manual-input`}
            />
            <div className="flex gap-2">
              <Button
                className="flex-1 font-bold"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                onClick={submitManual}
                data-testid={`${dataTestId}-manual-submit`}
              >
                Submit
              </Button>
              <Button
                variant="outline"
                className="border-[#C9A962] text-[#C9A962]"
                onClick={() => setMode('camera')}
                data-testid={`${dataTestId}-camera-toggle`}
              >
                <Camera className="h-4 w-4 mr-2" /> Use camera
              </Button>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white"
          onClick={() => onOpenChange?.(false)}
          data-testid={`${dataTestId}-close`}
        >
          <X className="h-4 w-4 mr-2" /> Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}
